import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Card, CardContent, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Pagination, Alert, Snackbar, CircularProgress, Tooltip, Divider, Accordion, AccordionSummary, AccordionDetails, Tabs, Tab, TablePagination, InputAdornment} from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon} from '@mui/icons-material';
import { onrcService, ONRCCompany } from '@services/onrcService';
import { formatForDisplay, formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`onrc-tabpanel-${index}`}
      aria-labelledby={`onrc-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ONRCManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [companies, setCompanies] = useState<ONRCCompany[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<ONRCCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCaenCode, setSelectedCaenCode] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [statistics, setStatistics] = useState<any>(null);
  const [caenCodes, setCaenCodes] = useState<any[]>([]);
  const [counties, setCounties] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<ONRCCompany | null>(null);
  const [editForm, setEditForm] = useState<Partial<ONRCCompany>>({});

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load filtered companies when filters change
  useEffect(() => {
    loadFilteredCompanies();
  }, [searchTerm, selectedCounty, selectedCity, selectedCaenCode, page, rowsPerPage]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load statistics
      const stats = await onrcService.getStatistics();
      setStatistics(stats);

      // Load CAEN codes
      const codes = await onrcService.getCaenCodes();
      setCaenCodes(codes);

      // Load counties from statistics
      if (stats.statisticiJudete) {
        setCounties(Object.keys(stats.statisticiJudete));
      }

      // Load initial companies
      await loadFilteredCompanies();
    } catch (error) {
      console.error('Eroare la incarcarea datelor initiale:', error);
      showSnackbar('Eroare la incarcarea datelor', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadFilteredCompanies = async () => {
    setLoading(true);
    try {
      const searchParams: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      if (searchTerm) {
        if (/^\d+$/.test(searchTerm)) {
          searchParams.cui = searchTerm;
        } else {
          searchParams.denumire = searchTerm;
        }
      }

      if (selectedCounty) searchParams.judet = selectedCounty;
      if (selectedCity) searchParams.localitate = selectedCity;
      if (selectedCaenCode) searchParams.codCAEN = selectedCaenCode;

      const response = await onrcService.searchCompanies(searchParams);
      setCompanies(response.data);
      setFilteredCompanies(response.data);
      setTotalCompanies(response.total);
    } catch (error) {
      console.error('Eroare la incarcarea firmelor:', error);
      showSnackbar('Eroare la incarcarea firmelor', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadFilteredCompanies();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCounty('');
    setSelectedCity('');
    setSelectedCaenCode('');
    setPage(0);
  };

  const handleEditCompany = (company: ONRCCompany) => {
    setSelectedCompany(company);
    setEditForm({
      denumire: company.denumire,
      cui: company.cui,
      formaJuridica: company.formaJuridica,
      judet: company.judet,
      localitate: company.localitate,
      strada: company.strada,
      numar: company.numar,
      codPostal: company.codPostal
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedCompany) return;

    try {
      // Aici ar trebui sa fie un endpoint pentru actualizarea firmelor
      // Pentru moment, doar simulam
      showSnackbar('Functionalitatea de editare va fi implementata in curand', 'info');
      setEditDialogOpen(false);
    } catch (error) {
      showSnackbar('Eroare la salvarea modificarilor', 'error');
    }
  };

  const handleDeleteCompany = async (company: ONRCCompany) => {
    if (window.confirm(`Sigur doriti sa stergeti firma "${company.denumire}"?`)) {
      try {
        // Aici ar trebui sa fie un endpoint pentru stergerea firmelor
        // Pentru moment, doar simulam
        showSnackbar('Functionalitatea de stergere va fi implementata in curand', 'info');
      } catch (error) {
        showSnackbar('Eroare la stergerea firmei', 'error');
      }
    }
  };

  const handleExportData = async () => {
    try {
      // Aici ar trebui sa fie un endpoint pentru export
      // Pentru moment, doar simulam
      showSnackbar('Functionalitatea de export va fi implementata in curand', 'info');
    } catch (error) {
      showSnackbar('Eroare la exportul datelor', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            <BusinessIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Administrare Firme ONRC
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestionarea firmelor veterinare din Registrul Comertului
          </Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportData}
            sx={{ mr: 2 }}
          >
            Export Date
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadInitialData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} mb={4}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Firme
                </Typography>
                <Typography variant="h4" component="div">
                  {statistics.totalFirme?.toLocaleString() || '0'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Coduri CAEN
                </Typography>
                <Typography variant="h4" component="div">
                  {statistics.coduriCAENRelevante?.length || '0'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Judete Acoperite
                </Typography>
                <Typography variant="h4" component="div">
                  {Object.keys(statistics.statisticiJudete || {}).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Ultima Actualizare
                </Typography>
                <Typography variant="h6" component="div">
                  {formatDateOnly(statistics.dataGenerare)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Firme" />
          <Tab label="Statistici" />
          <Tab label="Coduri CAEN" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* Search and Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Cautare CUI sau denumire"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )}}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Judet</InputLabel>
                  <Select
                    value={selectedCounty}
                    onChange={(e) => setSelectedCounty(e.target.value)}
                    label="Judet"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {counties.map((county) => (
                      <MenuItem key={county} value={county}>
                        {county}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Cod CAEN</InputLabel>
                  <Select
                    value={selectedCaenCode}
                    onChange={(e) => setSelectedCaenCode(e.target.value)}
                    label="Cod CAEN"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {caenCodes.map((code) => (
                      <MenuItem key={code.cod} value={code.cod}>
                        {code.cod} - {code.descriere}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  Cauta
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                >
                  Curata
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Companies Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Denumire</TableCell>
                    <TableCell>CUI</TableCell>
                    <TableCell>Judet</TableCell>
                    <TableCell>Localitate</TableCell>
                    <TableCell>Coduri CAEN</TableCell>
                    <TableCell>Actiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredCompanies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Nu s-au gasit firme
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCompanies.map((company) => (
                      <TableRow key={company.codInmatriculare}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {company.denumire}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {company.formaJuridica}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={company.cui} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Chip label={company.judet} size="small" color="secondary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {company.localitate}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {company.coduriCAENRelevante?.split(';').map((code, index) => (
                              <Chip
                                key={index}
                                label={code.trim()}
                                size="small"
                                variant="outlined"
                                color="success"
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Editeaza">
                              <IconButton
                                size="small"
                                onClick={() => handleEditCompany(company)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sterge">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteCompany(company)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCompanies}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Randuri per pagina:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} din ${count !== -1 ? count : `mai mult de ${to}`}`
              }
            />
          </CardContent>
        </Card>
      </TabPanel>

      {/* Statistics Tab */}
      <TabPanel value={tabValue} index={1}>
        {statistics && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Statistici pe Coduri CAEN
                  </Typography>
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {Object.entries(statistics.statisticiCAEN || {}).map(([code, count]) => (
                      <Box key={code} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          {code}: {caenCodes.find(c => c.cod === code)?.descriere || 'Necunoscut'}
                        </Typography>
                        <Chip label={count as number} size="small" color="primary" />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Statistici pe Judete
                  </Typography>
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {Object.entries(statistics.statisticiJudete || {})
                      .sort(([,a], [,b]) => (b as number) - (a as number))
                      .map(([county, count]) => (
                        <Box key={county} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{county}</Typography>
                          <Chip label={count as number} size="small" color="secondary" />
                        </Box>
                      ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* CAEN Codes Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Coduri CAEN Relevante pentru Domeniul Veterinar
            </Typography>
            <Grid container spacing={2}>
              {caenCodes.map((code) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={code.cod}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        {code.cod}
                      </Typography>
                      <Typography variant="body2">
                        {code.descriere}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editeaza Firma</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Denumire"
                value={editForm.denumire || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, denumire: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="CUI"
                value={editForm.cui || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, cui: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Forma juridica"
                value={editForm.formaJuridica || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, formaJuridica: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Judet"
                value={editForm.judet || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, judet: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Localitate"
                value={editForm.localitate || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, localitate: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Strada"
                value={editForm.strada || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, strada: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Numar"
                value={editForm.numar || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, numar: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Cod postal"
                value={editForm.codPostal || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, codPostal: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Anuleaza</Button>
          <Button onClick={handleSaveEdit} variant="contained">Salveaza</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ONRCManagement; 
